import { useState, useEffect } from 'react'
import { getSchools, createSchool, deleteSchool, updateSchool, School, getDatabases, clearDatabase, DatabaseInfo, getAcl, setAcl, getServerBoats } from '../../api/admin'
import { LogOut, Plus, Trash2, School as SchoolIcon, Edit2, X, KeyRound } from 'lucide-react'
import { useApp } from '../../store/AppContext'
import { clearToken } from '../../api/auth'
import ChangePasswordModal from '../Auth/ChangePasswordModal'

export default function AdminDashboard() {
    const { setCurrentSchool } = useApp()
    const [schools, setSchools] = useState<School[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [showForm, setShowForm] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [showChangePassword, setShowChangePassword] = useState(false)

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        username: '',
        password: '',
        firebaseUrl: '',
        googleEmails: ''
    })

    const [databases, setDatabases] = useState<DatabaseInfo[]>([])
    // Per-user boat allowlist (empty = unrestricted). allBoats = candidates for the picker.
    const [allBoats, setAllBoats] = useState<string[]>([])
    const [allowBoats, setAllowBoats] = useState<string[]>([])
    const [newBoat, setNewBoat] = useState('')

    useEffect(() => {
        loadSchools()
        loadDatabases()
        getServerBoats().then(setAllBoats).catch(() => {})
    }, [])

    async function loadSchools() {
        try {
            const data = await getSchools()
            setSchools(data)
        } catch (err: any) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    async function loadDatabases() {
        try { setDatabases(await getDatabases()) } catch { /* ignore */ }
    }

    async function handleClearDb(d: DatabaseInfo) {
        if (!confirm(`Clear ALL boat records for "${d.name}"? This frees space by deleting measurement history (schools/config kept).`)) return
        try {
            const r = await clearDatabase(d.kind, d.firebaseUrl)
            alert(`Cleared ${r.cleared} ${d.kind === 'server' ? 'boats' : 'boat measurement sets'}.`)
            loadDatabases()
        } catch (err: any) { alert('Clear failed: ' + err.message) }
    }

    const fmtBytes = (b: number) =>
        b >= 1e9 ? (b / 1e9).toFixed(2) + ' GB' : b >= 1e6 ? (b / 1e6).toFixed(1) + ' MB' : b >= 1e3 ? (b / 1e3).toFixed(0) + ' KB' : (b || 0) + ' B'

    function startEdit(school: School) {
        setEditingId(school.id)
        setFormData({
            name: school.name,
            username: school.username,
            password: '', // Don't show password
            firebaseUrl: school.firebaseUrl,
            googleEmails: school.googleEmails || ''
        })
        setAllowBoats([])
        getAcl(school.username).then(r => setAllowBoats(r.boats)).catch(() => setAllowBoats([]))
        setShowForm(true)
    }

    function cancelEdit() {
        setEditingId(null)
        setFormData({ name: '', username: '', password: '', firebaseUrl: '', googleEmails: '' })
        setAllowBoats([])
        setNewBoat('')
        setShowForm(false)
    }

    function toggleAllowBoat(id: string) {
        setAllowBoats(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id])
    }
    function addNewBoat() {
        const n = newBoat.trim()
        if (n && !allowBoats.includes(n)) setAllowBoats(prev => [...prev, n])
        setNewBoat('')
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()
        setError('')
        setLoading(true)

        try {
            if (editingId) {
                // Update existing
                const updateData: any = {
                    name: formData.name,
                    firebaseUrl: formData.firebaseUrl,
                    googleEmails: formData.googleEmails,
                }
                // Only send password if changed
                if (formData.password) {
                    updateData.password = formData.password
                }

                await updateSchool(editingId, updateData)
                await setAcl(formData.username, allowBoats)
            } else {
                // Create new
                await createSchool(formData)
            }

            cancelEdit()
            await loadSchools()
        } catch (err: any) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    async function handleDelete(id: number) {
        if (!confirm('Are you sure you want to delete this school?')) return

        try {
            await deleteSchool(id)
            await loadSchools()
        } catch (err: any) {
            alert(err.message)
        }
    }

    function handleLogout() {
        clearToken()
        setCurrentSchool(null)
    }

    return (
        <div className="min-h-screen bg-slate-900 p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
                        <p className="text-white/60">Manage schools and access</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowChangePassword(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors"
                        >
                            <KeyRound className="w-4 h-4" />
                            Change Password
                        </button>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                            Logout
                        </button>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                        {error}
                    </div>
                )}

                {/* Create School Button */}
                {!showForm && (
                    <button
                        onClick={() => setShowForm(true)}
                        className="mb-8 flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        Add New School
                    </button>
                )}

                {/* Create/Edit School Form */}
                {showForm && (
                    <div className="mb-8 glass-dark p-6 rounded-xl relative">
                        <button
                            onClick={cancelEdit}
                            className="absolute top-4 right-4 text-white/40 hover:text-white"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <h2 className="text-xl font-semibold text-white mb-4">
                            {editingId ? 'Edit School' : 'Add New School'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">School Name</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">Firebase URL</label>
                                    <input
                                        type="url"
                                        value={formData.firebaseUrl}
                                        onChange={e => setFormData({ ...formData, firebaseUrl: e.target.value })}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">Username</label>
                                    <input
                                        type="text"
                                        value={formData.username}
                                        onChange={e => setFormData({ ...formData, username: e.target.value })}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
                                        required
                                        disabled={!!editingId} // Cannot change username
                                        title={editingId ? "Username cannot be changed" : ""}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-1">
                                        {editingId ? 'New Password (leave blank to keep)' : 'Password'}
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.password}
                                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
                                        required={!editingId}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-white/80 mb-1">
                                        Google Sign-In Emails <span className="text-white/40">(optional — one per line; or "@domain.tld" to allow a whole domain)</span>
                                    </label>
                                    <textarea
                                        value={formData.googleEmails}
                                        onChange={e => setFormData({ ...formData, googleEmails: e.target.value })}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-sm"
                                        rows={3}
                                        placeholder={'teacher@school.lt\n@school.lt'}
                                    />
                                </div>
                            </div>

                            {editingId && (
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-white/80 mb-1">
                                        Visible boats — {allowBoats.length === 0 ? 'all (unrestricted)' : `${allowBoats.length} selected`}
                                    </label>
                                    <p className="text-xs text-white/40 mb-2">Pick which boats this user may see. Leave all unchecked = every boat. Cameras whose name matches a chosen boat come along automatically.</p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto p-1">
                                        {[...new Set([...allBoats, ...allowBoats])].sort().map(b => {
                                            const on = allowBoats.includes(b)
                                            return (
                                                <button type="button" key={b} onClick={() => toggleAllowBoat(b)}
                                                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-mono text-left border transition-colors ${on ? 'bg-green-500/15 border-green-500/40 text-green-300' : 'bg-white/5 border-white/10 text-white/60'}`}>
                                                    <span className="shrink-0">{on ? '☑' : '☐'}</span>
                                                    <span className="truncate">{b}</span>
                                                </button>
                                            )
                                        })}
                                        {allBoats.length === 0 && allowBoats.length === 0 && <span className="text-xs text-white/30 col-span-full py-2">No boats discovered — add names below.</span>}
                                    </div>
                                    <div className="flex gap-2 mt-2">
                                        <input value={newBoat} onChange={e => setNewBoat(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNewBoat() } }}
                                            placeholder="add boat name…"
                                            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white font-mono text-xs" />
                                        <button type="button" onClick={addNewBoat} className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs hover:bg-white/20">Add</button>
                                        {allowBoats.length > 0 && (
                                            <button type="button" onClick={() => setAllowBoats([])} className="px-3 py-1.5 text-blue-300/70 hover:text-blue-200 text-xs">Clear</button>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3 mt-6">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
                                >
                                    {loading ? 'Saving...' : (editingId ? 'Update School' : 'Create School')}
                                </button>
                                <button
                                    type="button"
                                    onClick={cancelEdit}
                                    className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Schools List */}
                <div className="grid gap-4">
                    {schools.map(school => (
                        <div key={school.id} className="glass-dark p-4 rounded-xl flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400">
                                    <SchoolIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-white">{school.name}</h3>
                                    <div className="flex gap-4 text-sm text-white/60">
                                        <span>@{school.username}</span>
                                        <span className="truncate max-w-xs" title={school.firebaseUrl}>{school.firebaseUrl}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => startEdit(school)}
                                    className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                                    title="Edit School"
                                >
                                    <Edit2 className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => handleDelete(school.id)}
                                    className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                    title="Delete School"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    ))}

                    {schools.length === 0 && !loading && (
                        <div className="text-center py-12 text-white/40">
                            No schools found. Create one to get started.
                        </div>
                    )}
                </div>

                {/* Databases — review sizes + clear records to free space after an event */}
                <div className="glass-dark p-6 rounded-xl mt-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-white">Databases</h2>
                        <button onClick={loadDatabases} className="text-white/60 hover:text-white text-sm">↻ Refresh</button>
                    </div>
                    <div className="space-y-2">
                        {databases.map((d, i) => (
                            <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                <div>
                                    <div className="text-white font-medium">{d.name} <span className="text-white/40 text-xs">({d.kind})</span></div>
                                    <div className="text-white/50 text-sm">{d.boats} boats · {d.records.toLocaleString()} records · {fmtBytes(d.estBytes)}</div>
                                </div>
                                <button onClick={() => handleClearDb(d)} className="px-3 py-1.5 bg-red-500/20 text-red-300 rounded-lg hover:bg-red-500/30 text-sm">Clear records</button>
                            </div>
                        ))}
                        {databases.length === 0 && <div className="text-white/40 text-center py-4">No databases.</div>}
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            {showChangePassword && (
                <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
            )}
        </div>
    )
}
